import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaEye, FaEyeSlash } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Customers.css';

const EditCustomer = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        city: '',
        zipCode: '',
        unitNumber: '',
        referralCode: '',
        password: '',
        loyaltyPoints: 0,
        loyaltyTier: 'bronze',
        isBlocked: false,
        blockedReason: '',
        isFlagged: false,
        flagReason: '',
        internalNotes: '',
        avatar: ''
    });

    useEffect(() => {
        fetchCustomer();
    }, [id]);

    const fetchCustomer = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const response = await api.get(`${ENDPOINTS.CUSTOMERS.base}/${id}`);
            const customer = response.data || response;
            setFormData({
                email: customer.email || '',
                firstName: customer.firstName || '',
                lastName: customer.lastName || '',
                phone: customer.phone || '',
                address: customer.address || '',
                city: customer.city || '',
                zipCode: customer.zipCode || '',
                unitNumber: customer.unitNumber || '',
                referralCode: customer.referralCode || '',
                password: '',
                loyaltyPoints: customer.loyaltyPoints || 0,
                loyaltyTier: customer.loyaltyTier || 'bronze',
                isBlocked: !!customer.isBlocked,
                blockedReason: customer.blockedReason || '',
                isFlagged: !!customer.isFlagged,
                flagReason: customer.flagReason || '',
                internalNotes: customer.internalNotes || '',
                avatar: customer.avatar || ''
            });
        } catch (error) {
            console.error('Failed to fetch customer:', error);
            toast.error('Failed to load customer details');
            navigate('/customers');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData };
            if (!payload.password) delete payload.password;
            
            // Ensure numeric types
            payload.loyaltyPoints = Number(payload.loyaltyPoints) || 0;

            await api.patch(`${ENDPOINTS.CUSTOMERS.base}/${id}`, payload);
            toast.success('Customer updated successfully!');
            navigate(`/customers/${id}`);
        } catch (error) {
            console.error('Failed to update customer:', error);
            toast.error('Failed to update customer: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    if (loading) {
        return (
            <div className="loading-spinner-container">
                <div className="modern-spinner"></div>
                <p className="loading-text">Loading member profile...</p>
            </div>
        );
    }

    return (
        <div className="create-customer-page edit-customer-page">
            <div className="page-header">
                <div className="header-left">
                    <button onClick={() => navigate(`/customers/${id}`)} className="btn-back">
                        <FaArrowLeft /> Back to Details
                    </button>
                    <h1>Edit Customer</h1>
                </div>
            </div>

            <div className="edit-grid">
                <form onSubmit={handleSubmit} className="customer-form form-grid">
                    {/* Basic Info Section */}
                    <div className="content-card">
                        <h3 className="card-title">Profile Information</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>First Name *</label>
                                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="form-control" required />
                            </div>
                            <div className="form-group">
                                <label>Last Name *</label>
                                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="form-control" required />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Email *</label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} className="form-control" required autoComplete="username" />
                            </div>
                            <div className="form-group">
                                <label>Phone *</label>
                                <input type="text" name="phone" value={formData.phone} onChange={handleChange} className="form-control" required />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>New Password (leave blank to keep current)</label>
                            <div className="password-input-wrapper" style={{ position: 'relative' }}>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="Enter new password"
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none' }}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>

                        <div className="form-group full-width" style={{ marginTop: '10px' }}>
                            <label>Avatar URL</label>
                            <input type="text" name="avatar" value={formData.avatar} onChange={handleChange} className="form-control" placeholder="Image URL for profile picture" />
                        </div>
                    </div>

                    {/* Address Section */}
                    <div className="content-card">
                        <h3 className="card-title">Primary Address</h3>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label>Address Line</label>
                                <input type="text" name="address" value={formData.address} onChange={handleChange} className="form-control" />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>City</label>
                                <input type="text" name="city" value={formData.city} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="form-group">
                                <label>Unit Number</label>
                                <input type="text" name="unitNumber" value={formData.unitNumber} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="form-group">
                                <label>Zip Code</label>
                                <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} className="form-control" />
                            </div>
                        </div>
                    </div>

                    {/* Admin/Internal Section */}
                    <div className="content-card">
                        <h3 className="card-title">Loyalty & Referrals</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Loyalty Points</label>
                                <input type="number" name="loyaltyPoints" value={formData.loyaltyPoints} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="form-group">
                                <label>Loyalty Tier</label>
                                <select name="loyaltyTier" value={formData.loyaltyTier} onChange={handleChange} className="form-control">
                                    <option value="bronze">Bronze</option>
                                    <option value="silver">Silver</option>
                                    <option value="gold">Gold</option>
                                    <option value="platinum">Platinum</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Referral Code (Own)</label>
                                <input type="text" name="referralCode" value={formData.referralCode} onChange={handleChange} className="form-control" readOnly />
                            </div>
                        </div>
                    </div>

                    {/* Risk & Blocking Section */}
                    <div className="content-card risk-management-card">
                        <h3 className="card-title">Account Status & Risk</h3>
                        <div className="status-checkboxes-row">
                            <div className="status-checkbox-item">
                                <label className="status-checkbox-label">
                                    <input type="checkbox" name="isBlocked" checked={formData.isBlocked} onChange={handleChange} />
                                    <span className="status-checkbox-text">
                                        <strong>Blocked</strong>
                                        <small>Restrict this customer from booking new services</small>
                                    </span>
                                </label>
                            </div>
                            {/* <div className="status-checkbox-item">
                                <label className="status-checkbox-label">
                                    <input type="checkbox" name="isFlagged" checked={formData.isFlagged} onChange={handleChange} />
                                    <span className="status-checkbox-text">
                                        <strong>Flagged</strong>
                                        <small>Mark profile for monitoring and review</small>
                                    </span>
                                </label>
                            </div> */}
                        </div>

                        {formData.isBlocked && (
                            <div className="form-group full-width">
                                <label>Blocked Reason</label>
                                <input type="text" name="blockedReason" value={formData.blockedReason} onChange={handleChange} className="form-control" placeholder="Enter reason for blocking..." />
                            </div>
                        )}

                        {formData.isFlagged && (
                            <div className="form-group full-width">
                                <label>Flagged Reason</label>
                                <input type="text" name="flagReason" value={formData.flagReason} onChange={handleChange} className="form-control" placeholder="Enter reason for flagging..." />
                            </div>
                        )}

                        <div className="form-group full-width" style={{ marginTop: '15px' }}>
                            <label>Internal Admin Notes</label>
                            <textarea name="internalNotes" value={formData.internalNotes} onChange={handleChange} className="form-control" rows="3" placeholder="Private internal notes for admins only..."></textarea>
                        </div>
                    </div>

                    <div className="form-actions" style={{ gridColumn: 'span 1 / -1', marginTop: '20px' }}>
                        <button type="submit" className="btn btn-primary btn-save" style={{ width: '100%', padding: '15px', fontSize: '1.1rem' }}>
                            <FaSave /> Update Customer Profile
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditCustomer;
